import { patientIdSchema } from "@repo/api/schemas";
import { createCarePlan, useCarePlans, useCases } from "@repo/data-store";
import { mintEntityId } from "@repo/domain";
import { Button } from "@repo/ui/components/ui/button";
import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useState } from "react";
import { CarePlanList, sortCarePlans } from "@/features/cases";

export const Route = createFileRoute("/patients/$patientId/_layout/care-plans/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { patientId } = Route.useParams();
  const id = patientIdSchema.parse(patientId);
  const [isCreating, setIsCreating] = useState(false);
  const [activeCase] = useCases({ patientId: id, status: "active" });
  const carePlans = useCarePlans({ patientId: id, kind: "case" });
  // Visit-derived plans surface under Visits, not here — list only standalone Case plans.
  const sorted = sortCarePlans(carePlans);
  async function handleCreate() {
    if (!activeCase || isCreating) return;
    setIsCreating(true);
    try {
      await createCarePlan({
        id: mintEntityId(),
        patient: { resourceType: "Patient", id },
        episodeOfCare: { resourceType: "EpisodeOfCare", id: activeCase.caseId },
      }).isPersisted.promise;
    } catch {
      // The shared outbox reports save failures.
    } finally {
      setIsCreating(false);
    }
  }

  const createButton = (
    <Button
      size="sm"
      disabled={!activeCase || isCreating}
      title={!activeCase ? "An active case is needed to create a care plan" : undefined}
      onClick={handleCreate}
    >
      <Plus />
      {isCreating ? "Creating…" : "Create care plan"}
    </Button>
  );

  return (
    <>
      {sorted.length === 0 ? (
        <CarePlanList.Empty>{createButton}</CarePlanList.Empty>
      ) : (
        <CarePlanList>
          <CarePlanList.Body>
            {sorted.map((cp) => (
              <CarePlanList.Item key={cp.carePlanId} carePlan={cp} patientId={patientId} fallbackTitle="Case plan" />
            ))}
          </CarePlanList.Body>
        </CarePlanList>
      )}
    </>
  );
}
